import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import TaxTablePage from "../../entities/TaxTablePage";
import LoaderHelper from "../../utils/Loader/LoaderHelper";

const TaxTable = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [taxPeriod, setTaxPeriod] = useState();
  const [year, setYear] = useState();
  const [success, setSuccess] = useState(null);
  const [frequency, setFrequency] = useState("Monthly");
  const [ageGroupData, setAgeGroupData] = useState([]);
  const location = useLocation();
  const { taxCountry } = location.state || {};
  const { showAlert } = useAlert();

  useEffect(() => {
    getTaxTable(taxCountry);
    getTaxPeriod(taxCountry);
    getAgeGroupList();
  }, []);

  const handleTaxTable = async (
    min,
    max,
    fixedAmount,
    ratePercentage,
    country,
    datefrom,
    dateTo,
    textTableId,
    yearId,
    frequency
  ) => {
    setLoading(true);
    try {
      const result = await AuthService.handleTaxTable(
        min,
        max,
        fixedAmount,
        ratePercentage,
        country,
        datefrom,
        dateTo,
        textTableId,
        yearId,
        frequency
      );
      if (result?.success === true) {
        showAlert(result?.message, "success");
        const targetYear = yearId || year;
        if (targetYear) {
          setYear(targetYear);
          await getYearTaxTable(targetYear, taxCountry);
        } else {
          await getTaxTable(taxCountry);
        }
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getAgeGroupList = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAgeGroups();
      if (result?.success) {
        setAgeGroupData(result?.data);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getTaxTable = async (taxCountry, freq = frequency) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAdmTaxTable(taxCountry, freq);
      if (result?.success) {
        const responseData = result?.data;
        setData(responseData);
        setYear(responseData?.taxYearID || null);
      } else {
        showAlert(result?.message, "error");
        setData([]);
      }
    } catch (error) {
      setData([]);
    } finally {
      LoaderHelper.loaderStatus(false);
      setLoading(false);
    }
  };

  const getYearTaxTable = async (id, country) => {
    setLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getYearTaxTable(id, country);
      if (result?.success) {
        if (result?.data) {
          setData(result?.data);
        }
        setSuccess(result?.success);
      } else {
        showAlert(result?.message, "error");
        setSuccess(result?.success);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    } finally {
      LoaderHelper.loaderStatus(false);
      setLoading(false);
    }
  };

  const getTaxPeriod = async (currentTaxCountry) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getTaxPeriod(currentTaxCountry);
      if (result?.success) {
        setTaxPeriod(result?.data);
        const currentYear = new Date().getFullYear();
        const matchedYear = result?.data?.find((item) => {
          const startYear = dayjs(item?.start).year();
          return startYear === currentYear;
        });
        setYear(matchedYear ? matchedYear._id : result?.data?.[0]?._id || null);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handledeleteTextTable = async (taxTableId, slabId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteTextTable(taxTableId, slabId);
      if (result?.success) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getTaxTable(taxCountry);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message, "error");
    }
  };
  const handleDeleteAllTextTable = async (taxTableId, slabIds) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteAllTextTable(taxTableId, slabIds);
      if (result?.success) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getTaxTable(taxCountry);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message, "error");
    }
  };

  const handleEditTaxTable = async (
    slabId,
    textTabelId,
    min,
    max,
    fixedAmount,
    ratePercentage,
    taxCountryParam,
    yearIdParam
  ) => {
    setLoading(true);
    try {
      const result = await AuthService.handleEditTaxTable(
        slabId,
        min,
        max,
        fixedAmount,
        ratePercentage,
        textTabelId
      );
      if (result?.success === true) {
        const targetYear = yearIdParam || year;
        if (targetYear) {
          await getYearTaxTable(targetYear, taxCountryParam ?? taxCountry);
          setYear(targetYear);
        } else {
          await getTaxTable(taxCountryParam ?? taxCountry);
        }
        showAlert(result?.message, "success");
      } else {
        showAlert(result?.message, "error");
      }
    } catch (error) {
      showAlert(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTaxYear = async (start, end) => {
    setLoading(true);
    try {
      const result = await AuthService.handleAddTaxPeriod(start, end);
      if (result?.success === true) {
        getTaxPeriod(taxCountry);
        setLoading(false);
        showAlert(result?.message, "success");
      } else {
        setLoading(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false);
      showAlert(error?.message, "error");
    }
  };

  const handleImport = async (data) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleTaxTableImport(data);
      if (result?.success) {
        showAlert(result?.message || "Import completed successfully", "success");
        await getTaxTable(taxCountry); // Wait for data to be fetched
        LoaderHelper.loaderStatus(false);
      } else {
        showAlert(result?.message || "Import failed", "error");
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      showAlert(error?.message || "Import failed", "error");
      LoaderHelper.loaderStatus(false);
    }
  }

  const handleFrequencyChange = async (newFrequency) => {
    try {
      LoaderHelper.loaderStatus(true);
      setFrequency(newFrequency);
      await getTaxTable(taxCountry, newFrequency);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <>
      <TaxTablePage
        setYear={setYear}
        success={success}
        handleAddTaxYear={handleAddTaxYear}
        handleTaxTable={handleTaxTable}
        year={year}
        setData={setData}
        getYearTaxTable={getYearTaxTable}
        taxPeriod={taxPeriod}
        loading={loading}
        data={data}
        handledeleteTextTable={handledeleteTextTable}
        handleEditTaxTable={handleEditTaxTable}
        taxCountry={taxCountry}
        handleFrequencyChange={handleFrequencyChange}
        ageGroupData={ageGroupData}
        frequency={frequency}
        setFrequency={setFrequency}
        handleImport={handleImport}
        handleDeleteAllTextTable={handleDeleteAllTextTable}
      />
    </>
  );
};

export default TaxTable;
